import { useState } from 'react'
import { Modal } from '@unvld/ui'
import { updateGuestTags } from '@unvld/db'
import type { Guest } from '@unvld/db'
import { toast } from 'sonner'
import { X, Plus, AlertTriangle, Calendar, CreditCard, Eye, Clock } from 'lucide-react'

const COMMON_TAGS = ['VIP', 'regular', 'newcomer', 'blacklisted'] as const

const TAG_STYLES: Record<string, string> = {
  VIP: 'bg-purple-500/20 text-purple-400',
  blacklisted: 'bg-red-500/20 text-red-400',
  regular: 'bg-blue-500/20 text-blue-400',
  newcomer: 'bg-green-500/20 text-green-400',
}

const TYPE_LABELS: Record<string, string> = {
  couple: 'Paar',
  single_male: 'Einzelherr',
  single_female: 'Einzeldame',
  group: 'Gruppe',
  unknown: 'Unbekannt',
}

function getTagStyle(tag: string) {
  return TAG_STYLES[tag] || 'bg-white/10 text-white/70'
}

interface GuestDetailModalProps {
  guest: Guest | null
  open: boolean
  onClose: () => void
  onTagsUpdated: (guestId: string, tags: string[]) => void
}

export function GuestDetailModal({ guest, open, onClose, onTagsUpdated }: GuestDetailModalProps) {
  const [customTag, setCustomTag] = useState('')
  const [saving, setSaving] = useState(false)

  if (!guest) return null

  const tags = guest.tags || []

  const saveTags = async (newTags: string[]) => {
    setSaving(true)
    try {
      await updateGuestTags(guest.id, newTags)
      onTagsUpdated(guest.id, newTags)

      if (newTags.includes('blacklisted') && !tags.includes('blacklisted')) {
        toast.warning('Gast wird beim Check-in gewarnt', {
          icon: <AlertTriangle className="w-4 h-4" />,
        })
      }

      toast.success('Tags aktualisiert')
    } catch (err) {
      console.error(err)
      toast.error('Fehler beim Speichern der Tags')
    }
    setSaving(false)
  }

  const addTag = (tag: string) => {
    const trimmed = tag.trim()
    if (!trimmed || tags.includes(trimmed)) return
    saveTags([...tags, trimmed])
  }

  const removeTag = (tag: string) => {
    saveTags(tags.filter((t) => t !== tag))
  }

  const handleCustomTagSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (customTag.trim()) {
      addTag(customTag.trim())
      setCustomTag('')
    }
  }

  return (
    <Modal open={open} onClose={onClose} className="max-w-md">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h2 className="text-2xl font-bold text-white">{guest.pseudonym}</h2>
          <span className="inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/10 text-white/70">
            {TYPE_LABELS[guest.guest_type] || guest.guest_type}
          </span>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/5 rounded-lg p-3">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <Eye className="w-3.5 h-3.5" />
              Besuche
            </div>
            <p className="text-white font-semibold">{guest.visit_count}</p>
          </div>
          <div className="bg-white/5 rounded-lg p-3">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <CreditCard className="w-3.5 h-3.5" />
              Ausgaben
            </div>
            <p className="text-white font-semibold">{(guest.total_spend_cents / 100).toFixed(0)} &euro;</p>
          </div>
          <div className="bg-white/5 rounded-lg p-3">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <Calendar className="w-3.5 h-3.5" />
              Erster Besuch
            </div>
            <p className="text-white font-semibold text-sm">
              {guest.first_visit ? new Date(guest.first_visit).toLocaleDateString('de-DE') : '—'}
            </p>
          </div>
          <div className="bg-white/5 rounded-lg p-3">
            <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
              <Clock className="w-3.5 h-3.5" />
              Letzter Besuch
            </div>
            <p className="text-white font-semibold text-sm">
              {guest.last_visit ? new Date(guest.last_visit).toLocaleDateString('de-DE') : '—'}
            </p>
          </div>
        </div>

        {/* Tags */}
        <div>
          <h3 className="text-sm font-medium text-slate-400 mb-3">Tags</h3>

          {/* Current tags */}
          <div className="flex flex-wrap gap-2 mb-3 min-h-[32px]">
            {tags.length === 0 && (
              <span className="text-xs text-slate-500">Keine Tags vergeben</span>
            )}
            {tags.map((tag) => (
              <span
                key={tag}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ${getTagStyle(tag)}`}
              >
                {tag}
                <button
                  onClick={() => removeTag(tag)}
                  disabled={saving}
                  className="ml-0.5 hover:opacity-70 transition-opacity disabled:opacity-30"
                  aria-label={`Tag ${tag} entfernen`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>

          {/* Quick-add buttons */}
          <div className="flex flex-wrap gap-2 mb-3">
            {COMMON_TAGS.filter((t) => !tags.includes(t)).map((tag) => (
              <button
                key={tag}
                onClick={() => addTag(tag)}
                disabled={saving}
                className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium border border-dashed transition-colors disabled:opacity-30 ${
                  tag === 'blacklisted'
                    ? 'border-red-500/30 text-red-400/60 hover:bg-red-500/10 hover:text-red-400'
                    : tag === 'VIP'
                    ? 'border-purple-500/30 text-purple-400/60 hover:bg-purple-500/10 hover:text-purple-400'
                    : tag === 'regular'
                    ? 'border-blue-500/30 text-blue-400/60 hover:bg-blue-500/10 hover:text-blue-400'
                    : 'border-green-500/30 text-green-400/60 hover:bg-green-500/10 hover:text-green-400'
                }`}
              >
                <Plus className="w-3 h-3" />
                {tag}
              </button>
            ))}
          </div>

          {/* Custom tag input */}
          <form onSubmit={handleCustomTagSubmit} className="flex gap-2">
            <input
              type="text"
              value={customTag}
              onChange={(e) => setCustomTag(e.target.value)}
              placeholder="Eigener Tag..."
              className="flex-1 px-3 py-1.5 bg-white/5 border border-slate-800 rounded-lg text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <button
              type="submit"
              disabled={!customTag.trim() || saving}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-30 disabled:hover:bg-indigo-600 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Hinzufügen
            </button>
          </form>
        </div>
      </div>
    </Modal>
  )
}
